/**
 * CompuMe V4 storefront integrity guard
 * Targeted, low-overhead repairs for optional legacy Ella components.
 * Intentionally avoids document-wide MutationObservers.
 */
(function () {
  'use strict';

  var quickCartRequestToken = 0;

  function hardenPredictiveSearch() {
    if (!window.customElements || !customElements.get) return;

    var PredictiveSearch = customElements.get('predictive-search');
    if (!PredictiveSearch || !PredictiveSearch.prototype) return;

    var proto = PredictiveSearch.prototype;
    if (proto.__cmWidgetGuarded || typeof proto.renderQuickSearchProductBlock !== 'function') return;

    var original = proto.renderQuickSearchProductBlock;
    proto.renderQuickSearchProductBlock = function () {
      if (!this.searchResultsWidget) return;
      return original.apply(this, arguments);
    };
    proto.__cmWidgetGuarded = true;
  }

  function schedulePredictiveSearchGuard() {
    if (!window.customElements) return;
    hardenPredictiveSearch();
    if (typeof customElements.whenDefined === 'function' && !customElements.get('predictive-search')) {
      customElements.whenDefined('predictive-search').then(hardenPredictiveSearch).catch(function () {});
    }
  }

  function repairCustomerAccessibility(root) {
    root = root || document;
    if (!root.querySelectorAll) return;

    var loginEmail = root.querySelector('#CustomerEmail');
    if (loginEmail) {
      var loginForm = loginEmail.closest('form');
      var hasLoginError = !!(loginForm && loginForm.querySelector('.alertBox--error'));
      if (!hasLoginError) {
        loginEmail.removeAttribute('aria-invalid');
        loginEmail.removeAttribute('aria-describedby');
      }
    }

    var registerPassword = root.querySelector('#RegisterForm-password');
    if (registerPassword) {
      var registerForm = registerPassword.closest('form');
      var registerLabel = registerForm && registerForm.querySelector('label[for="password"]');
      if (registerLabel) registerLabel.setAttribute('for', 'RegisterForm-password');
    }

    var confirmation = root.querySelector('#password_confirmation');
    if (confirmation && !root.querySelector('#password_confirmation-error')) {
      confirmation.removeAttribute('aria-invalid');
      confirmation.removeAttribute('aria-describedby');
    }

    var orderRows = root.querySelectorAll('table.order-history tbody tr');
    Array.prototype.forEach.call(orderRows, function (row, index) {
      var firstCell = row.querySelector('td:first-child');
      if (!firstCell) return;
      var rowId = 'RowOrder-' + (index + 1);
      firstCell.id = rowId;
      var cells = row.querySelectorAll('td[headers]');
      Array.prototype.forEach.call(cells, function (cell) {
        var headers = String(cell.getAttribute('headers') || '');
        if (headers.indexOf('RowOrder') !== -1) {
          cell.setAttribute('headers', headers.replace(/\bRowOrder\b/g, rowId));
        }
      });
    });
  }

  function addAiPrivacyControls(root) {
    root = root || document;
    if (!root.querySelector) return;

    var panel = root.querySelector('#cm-panel') || document.querySelector('#cm-panel');
    if (!panel || panel.querySelector('[data-cm-privacy-controls]')) return;
    var poweredBar = panel.querySelector('.cm-powered-bar');
    if (!poweredBar || !poweredBar.parentNode) return;

    var wrap = document.createElement('div');
    wrap.setAttribute('data-cm-privacy-controls', 'true');
    wrap.setAttribute('role', 'note');
    wrap.style.cssText = 'display:flex;align-items:center;justify-content:space-between;gap:10px;padding:6px 12px 7px;border-top:1px solid #e2e8f0;background:#fff;font:10.5px/1.35 -apple-system,BlinkMacSystemFont,Segoe UI,Roboto,sans-serif;color:#64748b;';

    var note = document.createElement('span');
    note.textContent = 'Chat history is stored on this device for up to 24 hours.';

    var clear = document.createElement('button');
    clear.type = 'button';
    clear.setAttribute('data-cm-clear-chat', 'true');
    clear.setAttribute('aria-label', 'Clear AI chat history from this device');
    clear.textContent = 'Clear chat';
    clear.style.cssText = 'border:0;background:transparent;color:#1a3a6b;font:inherit;font-weight:700;padding:3px 2px;cursor:pointer;white-space:nowrap;text-decoration:underline;text-underline-offset:2px;';
    clear.addEventListener('click', function () {
      try {
        localStorage.removeItem('cm_chat_v3');
        localStorage.removeItem('cm_chat_v2');
      } catch (e) {}
      window.location.reload();
    });

    wrap.appendChild(note);
    wrap.appendChild(clear);
    poweredBar.parentNode.insertBefore(wrap, poweredBar);
  }

  function disableUnusedLegacyHomepageScrollHandler() {
    if (!document.body || !document.body.classList.contains('template-index')) return;
    if (document.querySelector('[data-scrolling]')) return;

    var handler = window.onscroll;
    if (typeof handler !== 'function') return;

    var source = '';
    try { source = Function.prototype.toString.call(handler); } catch (e) {}
    if (source.indexOf('loadScrolling') !== -1) {
      window.onscroll = null;
    }
  }

  function formatShopifyMoney(value) {
    if (value === null || value === undefined || value === '') return '';
    try {
      if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
        return window.Shopify.formatMoney(value, window.money_format || '{{amount}} JOD');
      }
    } catch (e) {}
    return '';
  }

  function formatJodDecimal(value) {
    var number = Number(value);
    if (!Number.isFinite(number)) return '';
    try {
      return number.toLocaleString('en-US', { minimumFractionDigits: 3, maximumFractionDigits: 3 }) + ' JOD';
    } catch (e) {
      return number.toFixed(3) + ' JOD';
    }
  }

  function safeStoreProductUrl(raw, handle) {
    var candidate = raw || (handle ? '/products/' + encodeURIComponent(handle) : '');
    if (!candidate) return '';
    try {
      var url = new URL(candidate, window.location.origin);
      if (url.origin !== window.location.origin) return '';
      return url.pathname + url.search + url.hash;
    } catch (e) {
      return '';
    }
  }

  function cartProductIds(root) {
    var ids = Object.create(null);
    if (!root || !root.querySelectorAll) return ids;
    Array.prototype.forEach.call(root.querySelectorAll('cart-update-quantity[data-product]'), function (node) {
      if (node.dataset.product) ids[String(node.dataset.product)] = true;
    });
    return ids;
  }

  function normalizeRecommendationProducts(payload, excluded) {
    var result = [];
    var products = payload && Array.isArray(payload.products) ? payload.products : [];
    for (var i = 0; i < products.length && result.length < 3; i++) {
      var product = products[i];
      if (!product || !product.id || excluded[String(product.id)] || product.available === false) continue;

      var image = '';
      if (typeof product.featured_image === 'string') image = product.featured_image;
      else if (product.featured_image && product.featured_image.url) image = product.featured_image.url;
      else if (product.featured_image && product.featured_image.src) image = product.featured_image.src;
      if (!image && Array.isArray(product.images) && product.images.length) {
        image = typeof product.images[0] === 'string' ? product.images[0] : (product.images[0].src || '');
      }

      var url = safeStoreProductUrl(product.url, product.handle);
      if (!image || !url) continue;

      result.push({
        id: String(product.id),
        title: String(product.title || ''),
        url: url,
        image: image,
        price: formatShopifyMoney(product.price)
      });
    }
    return result;
  }

  function normalizeDealsProducts(payload, excluded) {
    var result = [];
    var products = payload && Array.isArray(payload.products) ? payload.products : [];
    for (var i = 0; i < products.length && result.length < 3; i++) {
      var product = products[i];
      if (!product || !product.id || excluded[String(product.id)]) continue;

      var variants = Array.isArray(product.variants) ? product.variants : [];
      var variant = null;
      for (var j = 0; j < variants.length; j++) {
        if (variants[j] && variants[j].available) {
          variant = variants[j];
          break;
        }
      }
      if (!variant) continue;

      var image = '';
      if (Array.isArray(product.images) && product.images.length) {
        image = typeof product.images[0] === 'string' ? product.images[0] : (product.images[0].src || '');
      }
      var url = safeStoreProductUrl('', product.handle);
      if (!image || !url) continue;

      result.push({
        id: String(product.id),
        title: String(product.title || ''),
        url: url,
        image: image,
        price: formatJodDecimal(variant.price)
      });
    }
    return result;
  }

  function buildQuickCartRecommendationCard(item) {
    var link = document.createElement('a');
    link.className = 'cmv4-cart-rec';
    link.href = item.url;
    link.setAttribute('aria-label', item.title);

    var media = document.createElement('span');
    media.className = 'cmv4-cart-rec__media';
    var image = document.createElement('img');
    image.src = item.image;
    image.alt = item.title;
    image.loading = 'lazy';
    image.decoding = 'async';
    media.appendChild(image);

    var body = document.createElement('span');
    body.className = 'cmv4-cart-rec__body';
    var title = document.createElement('strong');
    title.className = 'cmv4-cart-rec__title';
    title.textContent = item.title;
    body.appendChild(title);

    if (item.price) {
      var price = document.createElement('span');
      price.className = 'cmv4-cart-rec__price';
      price.textContent = item.price;
      body.appendChild(price);
    }

    link.appendChild(media);
    link.appendChild(body);
    return link;
  }

  function renderQuickCartRecommendations(block, products, titleText, sourceProductId) {
    block.innerHTML = '';
    if (!products.length) {
      block.hidden = true;
      block.setAttribute('data-cm-state', 'empty');
      block.setAttribute('data-cm-source-product', sourceProductId);
      return;
    }

    var title = document.createElement('div');
    title.className = 'previewCartTitle cmv4-cart-recs__title text-left';
    title.textContent = titleText || 'You May Also Like';

    var grid = document.createElement('div');
    grid.className = 'cmv4-cart-recs__grid';
    products.forEach(function (product) {
      grid.appendChild(buildQuickCartRecommendationCard(product));
    });

    block.appendChild(title);
    block.appendChild(grid);
    block.hidden = false;
    block.setAttribute('data-cm-state', 'ready');
    block.setAttribute('data-cm-source-product', sourceProductId);
  }

  async function enhanceQuickCartRecommendations(root) {
    if (!root || !root.querySelector) return;
    var block = root.querySelector('.previewCartCollection');
    if (!block) return;

    var sourceItem = root.querySelector('cart-update-quantity[data-product]');
    var sourceProductId = sourceItem && sourceItem.dataset ? String(sourceItem.dataset.product || '') : '';
    if (!sourceProductId) {
      block.hidden = true;
      block.setAttribute('data-cm-state', 'empty');
      return;
    }

    var currentState = block.getAttribute('data-cm-state');
    if (block.getAttribute('data-cm-source-product') === sourceProductId && (currentState === 'ready' || currentState === 'empty' || currentState === 'loading')) return;

    var titleNode = block.querySelector('.previewCartTitle');
    var titleText = titleNode ? String(titleNode.textContent || '').trim() : 'You May Also Like';
    var excluded = cartProductIds(root);
    var token = ++quickCartRequestToken;

    block.hidden = true;
    block.setAttribute('data-cm-state', 'loading');
    block.setAttribute('data-cm-source-product', sourceProductId);

    var products = [];
    try {
      var recommendationResponse = await fetch('/recommendations/products.json?product_id=' + encodeURIComponent(sourceProductId) + '&limit=6&intent=related', { credentials: 'same-origin' });
      if (recommendationResponse.ok) {
        products = normalizeRecommendationProducts(await recommendationResponse.json(), excluded);
      }
    } catch (e) {}

    if (!products.length) {
      try {
        var dealsResponse = await fetch('/collections/deals/products.json?limit=12', { credentials: 'same-origin' });
        if (dealsResponse.ok) {
          products = normalizeDealsProducts(await dealsResponse.json(), excluded);
        }
      } catch (e) {}
    }

    if (token !== quickCartRequestToken || !document.documentElement.contains(block)) return;
    renderQuickCartRecommendations(block, products, titleText, sourceProductId);
  }

  function initQuickCartRecommendations() {
    var wrapper = document.querySelector('#halo-cart-sidebar .previewCart-wrapper');
    if (!wrapper) return;

    enhanceQuickCartRecommendations(wrapper);
    if (wrapper.__cmRecommendationsObserver || !('MutationObserver' in window)) return;

    var queued = false;
    var observer = new MutationObserver(function (mutations) {
      var changed = false;
      for (var i = 0; i < mutations.length; i++) {
        if (mutations[i].type === 'childList' && mutations[i].addedNodes.length) {
          changed = true;
          break;
        }
      }
      if (!changed || queued) return;
      queued = true;
      requestAnimationFrame(function () {
        queued = false;
        enhanceQuickCartRecommendations(wrapper);
      });
    });

    observer.observe(wrapper, { childList: true, subtree: true });
    wrapper.__cmRecommendationsObserver = observer;
  }

  function runRepairs(root) {
    repairCustomerAccessibility(root || document);
    addAiPrivacyControls(root || document);
  }

  function init() {
    runRepairs(document);
    schedulePredictiveSearchGuard();
    disableUnusedLegacyHomepageScrollHandler();
    initQuickCartRecommendations();

    if (window.Shopify && Shopify.designMode) {
      document.addEventListener('shopify:section:load', function (event) {
        runRepairs(event.target || document);
        schedulePredictiveSearchGuard();
        disableUnusedLegacyHomepageScrollHandler();
        initQuickCartRecommendations();
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
